import type { StatusMeaning, StatusTone } from '@league/shared/status';
import { clsx } from 'clsx';

/**
 * A status, spelled the one way it is spelled.
 *
 * This takes a `StatusMeaning` — a `{ label, tone }` from `@league/shared/status` — rather than a
 * free string, and that is the whole design. There is no prop to pass "Registering" through. The
 * mockups spell the same state three different ways in three different places; a component that
 * accepts arbitrary text would faithfully reproduce all three.
 *
 * Tone is not decoration. If it is coloured, it means something — so there are four tones and no
 * more, each with exactly one text token that may sit on it.
 */
const TONES: Record<StatusTone, string> = {
  neutral: 'bg-tint-deep text-ink-2',
  positive: 'bg-tint-sage text-sage',
  /** `on-tint-accent`, not `accent-text`: the ordinary accent text is too light on the signal tint. */
  accent: 'bg-tint-signal text-on-tint-signal',
  negative: 'bg-tint-rust text-rust',
};

export function StatusChip({ status, className }: { status: StatusMeaning; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex h-[22px] items-center rounded-chip px-2',
        'text-[11px] font-semibold uppercase tracking-[0.04em] whitespace-nowrap',
        TONES[status.tone],
        className,
      )}
    >
      {status.label}
    </span>
  );
}
